// Destinos de navegación (UX §2.2, DS §5.3 mapa de íconos) y sus colas (badges, UX §2.7).
import {
  CalendarDays,
  ChartColumn,
  Gift,
  Landmark,
  LifeBuoy,
  Package,
  Percent,
  Settings,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import type { QueuesResponse, Role, Vocab } from '@atte/contracts';
import { plural } from '@/components/ui/format';
import type { SectionKey } from '@/lib/permissions';
import { t } from '@/lib/i18n/es-AR';

export const SECTION_ICONS: Record<SectionKey, LucideIcon> = {
  agenda: CalendarDays,
  caja: Wallet,
  clientes: Users,
  'gift-cards': Gift,
  stock: Package,
  comisiones: Percent,
  finanzas: Landmark,
  tablero: ChartColumn,
  ajustes: Settings,
  ayuda: LifeBuoy,
};

/** Etiqueta del destino con el vocabulario del rubro ("Pacientes" en salud). */
export function sectionLabel(section: SectionKey, vocab: Vocab, _role?: Role): string {
  if (section === 'clientes') return vocab.ClientPl;
  return t.nav[section];
}

export interface SectionBadge {
  count: number;
  /** Texto accesible pluralizado ("3 turnos sin cerrar", UX-P13). */
  label: string;
}

/** Badge de cada destino a partir de las colas (solo valores > 0). */
export function badgesFromQueues(q: QueuesResponse | undefined, salesOn: boolean): Partial<Record<SectionKey, SectionBadge>> {
  if (!q) return {};
  const out: Partial<Record<SectionKey, SectionBadge>> = {};
  const unclosed = q.unclosedAppointments ?? 0;
  const cashDays = q.unclosedCashDays?.length ?? 0;
  const unclosedText = unclosed > 0 ? plural(unclosed, 'turno sin cerrar', 'turnos sin cerrar') : '';
  if (salesOn) {
    const parts = [unclosedText, cashDays > 0 ? plural(cashDays, 'día de caja sin cerrar', 'días de caja sin cerrar') : ''].filter(Boolean);
    if (unclosed + cashDays > 0) out.caja = { count: unclosed + cashDays, label: parts.join(' · ') };
  } else if (unclosed > 0) {
    // Sin Caja, los turnos sin cerrar se muestran en Agenda (UX §2.7).
    out.agenda = { count: unclosed, label: unclosedText };
  }
  const low = q.lowStock?.count ?? 0;
  if (low > 0) out.stock = { count: low, label: plural(low, 'producto para reponer', 'productos para reponer') };
  const overdue = q.overdueFixedCosts?.count ?? 0;
  if (overdue > 0) out.finanzas = { count: overdue, label: plural(overdue, 'gasto fijo vencido', 'gastos fijos vencidos') };
  const stale = q.pendingCommissions?.staleCount ?? 0;
  if (stale > 0) out.comisiones = { count: stale, label: plural(stale, 'liquidación pendiente', 'liquidaciones pendientes') };
  return out;
}
